import React, { useEffect, PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { Spin } from 'antd';
import { selectUser } from 'redux/user/selectors';
import { identifyUser } from 'utils/reporting';
import { initUserflow } from 'utils/userflow';
import { useBackgroundTasksStream } from 'hooks/useBackgroundTasksStream';
import { useExportTaskCompleteEvent } from 'hooks/useExportTaskCompleteEvent';
import { usePolicyManagerCompleteEvent } from 'hooks/usePolicyManagerCompleteEvent';

/**
 * The authenticated app chrome of the AMS mode: mounted only while the session
 * outcome is minted, so a cross-tab sign-out (which swaps the gate to the
 * SignedOut screen without navigating) stands these services down. Renders a
 * spinner until the user lands in Redux: the gate is open but the post-mint
 * user load is still in flight.
 * useRefreshToken is deliberately absent: RenewalPolicy owns AMS renewal.
 */
export function AuthedAppServices({ children }: PropsWithChildren) {
  const user = useSelector(selectUser);

  useBackgroundTasksStream();
  useExportTaskCompleteEvent();
  usePolicyManagerCompleteEvent();

  useEffect(() => {
    if (user) {
      identifyUser(user);
      initUserflow(user);
    }
  }, [user]);

  return user ? <>{children}</> : <Spin data-testid="auth-user-loading" />;
}
